/**
 * Feed API Utility
 * 
 * Client-side API layer for the server-driven feed.
 * All feed operations go through the server instead of direct Firestore access.
 * 
 * This module handles:
 * - Auth token injection
 * - Session creation (new shuffle seed on reload)
 * - Cursor-based pagination
 * - Like/share/play/comment operations
 * - Impression tracking
 */

import { auth } from '../firebase';

const API_BASE = window.location.hostname === 'localhost'
    ? 'http://localhost:5001/api'
    : 'https://us-central1-joynet-99b18.cloudfunctions.net/app/api';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Get the Firebase auth token for the current user.
 */
async function getAuthToken() {
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');
    return user.getIdToken();
}

/**
 * Make an authenticated request to the feed API.
 */
async function apiFetch(path, options = {}) {
    const token = await getAuthToken();
    const url = `${API_BASE}${path}`;

    const response = await fetch(url, {
        ...options,
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            ...options.headers
        }
    });

    const data = await response.json();

    if (!response.ok) {
        const error = new Error(data.message || 'API request failed');
        error.status = response.status;
        error.data = data;
        throw error;
    }

    return data;
}

// ---------------------------------------------------------------------------
// Feed Session
// ---------------------------------------------------------------------------

/**
 * Create a new feed session (new shuffle seed).
 * Call this on page load / reload to get fresh ordering.
 * 
 * @returns {{ sessionId: string, seed: string, expiresAt: number }}
 */
export async function createFeedSession() {
    const data = await apiFetch('/feed/session', { method: 'POST' });
    return data.session;
}

// ---------------------------------------------------------------------------
// Feed Retrieval (Cursor-based pagination)
// ---------------------------------------------------------------------------

/**
 * Fetch a page of feed content.
 * 
 * First page: pass `seed` from createFeedSession()
 * Subsequent pages: pass `cursor` from previous response
 * 
 * @param {Object} params
 * @param {string} [params.seed] - Shuffle seed (first page)
 * @param {string} [params.cursor] - Cursor from previous page
 * @param {number} [params.pageSize] - Items per page (default 15, max 50)
 * @param {string} [params.initialPostId] - Pin a specific post to top of first page
 * @returns {{ posts: Array, nextCursor: string|null, hasMore: boolean }}
 */
export async function fetchFeed({ seed, cursor, pageSize, initialPostId } = {}) {
    const params = new URLSearchParams();
    if (seed) params.set('seed', seed);
    if (cursor) params.set('cursor', cursor);
    if (pageSize) params.set('pageSize', String(pageSize));
    if (initialPostId) params.set('initialPostId', initialPostId);

    const data = await apiFetch(`/feed?${params.toString()}`);
    return {
        posts: data.posts,
        nextCursor: data.nextCursor,
        hasMore: data.hasMore
    };
}

// ---------------------------------------------------------------------------
// Impressions
// ---------------------------------------------------------------------------

/**
 * Record that the user saw a set of posts.
 * 
 * @param {string} sessionId
 * @param {string[]} postIds
 */
export async function recordImpressions(sessionId, postIds) {
    await apiFetch('/feed/impression', {
        method: 'POST',
        body: JSON.stringify({ sessionId, postIds })
    });
}

// ---------------------------------------------------------------------------
// Likes
// ---------------------------------------------------------------------------

/**
 * Toggle like on a post.
 * 
 * @param {string} postId
 * @param {string} source - Post source (tiktok, youtube)
 * @returns {{ liked: boolean, stats: Object, likedVideos: string[] }}
 */
export async function toggleLike(postId, source) {
    const data = await apiFetch('/feed/like', {
        method: 'POST',
        body: JSON.stringify({ postId, source })
    });
    return data;
}

/**
 * Get the current user's liked video IDs.
 * 
 * @returns {string[]}
 */
export async function getLikedVideos() {
    const data = await apiFetch('/feed/liked');
    return data.likedVideos;
}

// ---------------------------------------------------------------------------
// Plays
// ---------------------------------------------------------------------------

/**
 * Record a play event for a post.
 * 
 * @param {string} postId
 * @returns {{ stats: Object }}
 */
export async function recordPlay(postId) {
    const data = await apiFetch('/feed/play', {
        method: 'POST',
        body: JSON.stringify({ postId })
    });
    return data;
}

// ---------------------------------------------------------------------------
// Shares
// ---------------------------------------------------------------------------

/**
 * Record a share event for a post.
 * 
 * @param {string} postId
 * @returns {{ stats: Object }}
 */
export async function recordShare(postId) {
    const data = await apiFetch('/feed/share', {
        method: 'POST',
        body: JSON.stringify({ postId })
    });
    return data;
}

// ---------------------------------------------------------------------------
// Comments
// ---------------------------------------------------------------------------

/**
 * Submit a comment on a post.
 * 
 * @param {string} postId
 * @param {string} text
 * @returns {{ comment: Object, comments: Array, stats: Object }}
 */
export async function addComment(postId, text) {
    const data = await apiFetch('/feed/comment', {
        method: 'POST',
        body: JSON.stringify({ postId, text })
    });
    return data;
}

/**
 * Get comments for a post.
 * 
 * @param {string} postId
 * @returns {Array}
 */
export async function getComments(postId) {
    const data = await apiFetch(`/feed/comments/${encodeURIComponent(postId)}`);
    return data.comments;
}

// ---------------------------------------------------------------------------
// Report
// ---------------------------------------------------------------------------

/**
 * Report content.
 * 
 * @param {string} postId
 * @param {string} platform
 */
export async function reportContent(postId, platform) {
    await apiFetch('/feed/report', {
        method: 'POST',
        body: JSON.stringify({ postId, platform })
    });
}